/**
 * @file        src/pages/blog/BlogList.jsx
 * @description Blog List. Columns: # · Image · Title · Category · Status ·
 *              Language · Updated On · Action.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (Blog Management).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, del } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function BlogList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');

  const qc = useQueryClient();
  const params = { page, pageSize };
  if (applied) params.q = applied;

  const { data, isLoading } = useQuery({
    queryKey: qk.blogs.list(params),
    queryFn: () => get('/blogs', { params }),
    placeholderData: keepPreviousData,
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/blogs/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Blog removed.');
      qc.invalidateQueries({ queryKey: ['blogs'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  async function onDelete(row) {
    if (!(await confirm(`Delete blog “${row.title}”?`))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead
        title="Blog List"
        subtitle="Blog Management · posts"
        actions={<Link to="/blog/posts/new" className="btn primary"><Icon name="plus" /> Add Blog</Link>}
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
          <div className="field">
            <label>Title</label>
            <input placeholder="Search blog…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Image</th><th>Title</th><th>Category</th><th>Status</th><th>Language</th><th>Updated On</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="empty">No blogs yet. Add your first one.</td></tr>
                ) : rows.map((b, idx) => (
                  <tr key={b.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td>{b.image
                      ? <img src={b.image} alt={b.title} style={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                      : <span className="muted">—</span>}</td>
                    <td><b>{b.title}</b></td>
                    <td>{b.category_name || <span className="muted">—</span>}</td>
                    <td><Chip tone={b.is_active ? 'ok' : 'rej'}>{b.is_active ? 'Active' : 'Inactive'}</Chip></td>
                    <td className="mono">{b.language}</td>
                    <td>{fmtDate(b.updated_at)}</td>
                    <td className="row-act">
                      <Link to={`/blog/posts/${b.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(b)}><Icon name="trash" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
